/**
 * Wires `consign.model.ts` to the consignment doors on one work.
 *
 * Calls `fetchWorkConsignment` for the row, `offerConsignment` and
 * `endConsignment` to change it — each followed by a re-read, since the
 * gallery's answer is half of the state and only Postgres holds both — and
 * `checkEligibility` on blur of the id field, so the reader meets the
 * server's refusal once rather than twice. A refused read is the ordinary
 * case for a viewer and lands as "not with a gallery".
 *
 * The state the model cannot hold is the whole form: open, draft, the
 * on-blur verdict, the problem, sending and sent, the two-press end, and the
 * reload counter. The verdict is dropped the moment the id changes, because
 * it was about the id that was there before.
 */

import { useCallback, useEffect, useState } from "react";
import type { WorkConsignment } from "@ondo/shared";

import {
  checkEligibility,
  endConsignment,
  fetchWorkConsignment,
  offerConsignment,
  setConsignmentLocationSharing,
} from "../lib/api.ts";
import { CONSIGN_COPY } from "./consign.constants.ts";
import {
  EMPTY_OFFER,
  describeConsignError,
  isOndoIdShape,
  toConsignPanelView,
  toOfferRequest,
} from "./consign.model.ts";
import type { ConsignPanelView, GalleryCheck, OfferDraft } from "./consign.model.ts";

export interface ConsignController {
  view: ConsignPanelView;
  /** The offer form: open, typed, checked, sent. */
  form: {
    open: boolean;
    draft: OfferDraft;
    check: GalleryCheck;
    problem: string | null;
    sending: boolean;
    sent: boolean;
  };
  openForm: () => void;
  closeForm: () => void;
  setDraft: (patch: Partial<OfferDraft>) => void;
  /** On blur of the id field: ask the server whether it names a gallery. */
  checkGallery: () => void;
  offer: () => void;
  /** Ending, in two presses. */
  askToEnd: () => void;
  cancelEnd: () => void;
  end: () => void;
  confirmingEnd: boolean;
  ending: boolean;
  /** The owner's location switch — OND-88. One press, and the row is re-read. */
  setLocationSharing: (shared: boolean) => void;
  savingLocationSharing: boolean;
  error: string | null;
}

/**
 * A work's consignment, from the owner's or the agent's side.
 *
 * ONE READ, TWO WRITES, both re-read afterwards. `fetchWorkConsignment` is
 * the owner-side reading of the same row the gallery's inventory shows; the
 * writes are the two doors that had no caller. After either, the row is read
 * back rather than patched — a consignment's state is decided by the gallery's
 * answer as much as by ours, and only Postgres holds both.
 *
 * THE ELIGIBILITY CHECK ON BLUR is the agent modal's pattern exactly: the
 * server answers with the SAME sentence the write would raise, so a reader
 * who ignores it and presses Send does not meet two explanations of one
 * refusal. A failed check is silent — it is a courtesy.
 *
 * @param canAct whether the reader may offer or end at all. Decides only what
 *   is drawn; the definers decide what is honoured.
 */
export function useConsignController(workId: string, canAct: boolean): ConsignController {
  const [consignment, setConsignment] = useState<WorkConsignment | null | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [draft, setDraftState] = useState<OfferDraft>(EMPTY_OFFER);
  const [check, setCheck] = useState<GalleryCheck>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  const [ending, setEnding] = useState(false);
  const [savingLocationSharing, setSavingLocationSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let active = true;

    fetchWorkConsignment(workId)
      .then((found) => {
        if (active) setConsignment(found);
      })
      // A refusal here is the ordinary case for a reader who may not see the
      // owner's arrangements — a viewer, an agent below the owner. The panel
      // then says "not with a gallery", which is what they may know.
      .catch(() => {
        if (active) setConsignment(null);
      });

    return () => {
      active = false;
    };
  }, [workId, nonce]);

  const setDraft = useCallback((patch: Partial<OfferDraft>) => {
    setDraftState((current) => ({ ...current, ...patch }));
    setProblem(null);
    // The verdict was about the id that has just changed, so it is dropped.
    if ("ondoId" in patch) setCheck(null);
  }, []);

  return {
    view: toConsignPanelView(consignment, canAct),
    form: { open, draft, check, problem, sending, sent },
    confirmingEnd,
    ending,
    savingLocationSharing,
    error,

    setLocationSharing: (shared) => {
      if (!consignment) return;
      setSavingLocationSharing(true);
      setError(null);

      // Re-read rather than patch, as every other write here does: the switch
      // the panel draws is the row's, and the row is the server's.
      setConsignmentLocationSharing(consignment.consignmentId, { shared })
        .then(() => setNonce((n) => n + 1))
        .catch((cause: unknown) => setError(describeConsignError(cause)))
        .finally(() => setSavingLocationSharing(false));
    },

    openForm: () => {
      setOpen(true);
      setSent(false);
      setError(null);
    },
    closeForm: () => {
      setOpen(false);
      setDraftState(EMPTY_OFFER);
      setCheck(null);
      setProblem(null);
    },
    setDraft,

    checkGallery: () => {
      const typed = draft.ondoId.trim();
      if (typed.length === 0 || !isOndoIdShape(typed)) return;

      setCheck({ state: "checking" });
      void checkEligibility({
        ondoId: typed,
        capacity: "gallery",
        subject: { kind: "work", id: workId },
      })
        .then((answer) => {
          setCheck(
            answer.verdict === "eligible" && answer.principal
              ? { state: "eligible", displayName: answer.principal.displayName }
              : { state: "refused", message: answer.message ?? CONSIGN_COPY.errors.invalidId },
          );
        })
        .catch(() => setCheck(null));
    },

    offer: () => {
      const parsed = toOfferRequest(draft);
      if (!parsed.ok) {
        setProblem(parsed.problem);
        return;
      }

      setSending(true);
      setError(null);

      offerConsignment(workId, parsed.body)
        .then(() => {
          setSent(true);
          setOpen(false);
          setDraftState(EMPTY_OFFER);
          setCheck(null);
          setNonce((n) => n + 1);
        })
        .catch((cause: unknown) => setError(describeConsignError(cause)))
        .finally(() => setSending(false));
    },

    askToEnd: () => {
      setError(null);
      setConfirmingEnd(true);
    },
    cancelEnd: () => setConfirmingEnd(false),
    end: () => {
      if (!consignment) return;
      setEnding(true);
      setError(null);

      // `withdrawn` before the gallery answers; `returned` after. The definer
      // refuses `declined` — that is the gallery's answer, not the owner's act.
      endConsignment(consignment.consignmentId, {
        outcome: consignment.acceptedAt === null ? "withdrawn" : "returned",
      })
        .then(() => {
          setConfirmingEnd(false);
          setNonce((n) => n + 1);
        })
        .catch((cause: unknown) => setError(describeConsignError(cause)))
        .finally(() => setEnding(false));
    },
  };
}
